import { Button } from "components"
import React, { useState } from "react"

import { useLocation, useNavigate } from "react-router-dom"

import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { Input } from "antd"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  PERFORMANCE_ENDPOINTS,
  performanceService,
} from "services/performanceService"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore, useAuthStore } from "stores"
import { LoadingIndicatorWhite } from "components/custom/LoaderWhite"
import getIdFromKey from "utils/getIdFromKey"
import { IEmpReviewListing } from "pages/dashboard/tasks/performance/reviews/reviews/interface/reviews"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import NewModalWrapper from "components/new-stuff/ui/modals/NewModalWrapper"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"

const ViewReviewListing: React.FC<{}> = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()
  const myParam = useLocation().search
  const { user } = useAuthStore()
  const x = new URLSearchParams(myParam).get("x")
  const [search, setSearch] = useState("")
  const [activeReviewId, setActiveReviewId] = useState("")
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [activeDeletId, setActiveDeleteId] = useState("")

  const {
    data: reviewRoundsList,
    isFetching: isReviewRoundsLoading,
    refetch,
  } = useQuery<IEmpReviewListing>({
    queryFn: () =>
      performanceService
        .selectReviewRounds({
          person_id: getIdFromKey(user?.personKey || "") ?? "",
        })
        .then((res) => res as IEmpReviewListing),
    queryKey: [PERFORMANCE_ENDPOINTS.SELECT_REVIEW_ROUNDS, user?.personKey],
    keepPreviousData: true,
    onSuccess: () => {
      mixPanelEvent("empl-pf-review-listing")
    },
  })

  const TableData =
    reviewRoundsList?.data?.data
      ?.filter((rev) => {
        const name = rev.review_group_qualified_description
        return name.toLowerCase().includes(search.toLowerCase())
      })
      .map((review, index) => {
        return {
          key: review.review_group_id,
          id: review.review_group_id,
          period: {
            title: review?.period,
            date: review?.review_group_start_date
              ? `(${review.review_group_start_date}-${review.review_group_due_date})`
              : `-`,
          },
          title: review.review_group_qualified_description,
          status: review.review_group_state,
          action: review.review_group_key,
        }
      }) || []

  const TableColumns = [
    {
      title: "Title",
      dataIndex: "title",
      render: (title: string) => (
        <span className="font-avenir text-sm  text-[#42526D]">{title}</span>
      ),
    },
    {
      title: "Review Period",
      dataIndex: "period",
      render: (period: { title: string; date: string }) => (
        <div className="">
          <div className={`text-sm font-avenir  text-[#42526D] `}>
            {period?.title}
          </div>
          <p className="text-xs text-padeSubtext">{period?.date}</p>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (statusText: string) => (
        <span
          className={` text-sm white  font-medium py-1 block text-center w-full rounded-[4px] font-avenir ${
            statusText === "Ready"
              ? "bg-success100 text-success800"
              : statusText === "Archived"
              ? "bg-danger100 text-danger500"
              : "bg-[#FEE3B1] text-[#E69702] w-full"
          }`}
        >
          {statusText}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      render: (id: string) => <TableActionSelect items={actionItems(id)} />,
    },
  ]
  const actionItems = (id: string) => [
    {
      key: "1",
      label: <button onClick={() => handleClick(id)}>View</button>,
    },
    // {
    //   key: "2",
    //   label: (
    //     <button
    //       onClick={() => {
    //         setActiveDeleteId(getIdFromKey(id))
    //         setOpenDeleteConfirmation(true)
    //       }}
    //     >
    //       Delete
    //     </button>
    //   ),
    // },
  ]
  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [PERFORMANCE_ENDPOINTS.ADMIN_SELECT_REVIEW_SETTINGS],
    })
    queryClient.invalidateQueries({
      queryKey: [PERFORMANCE_ENDPOINTS.SELECT_REVIEW_ROUNDS],
    })
  }
  const { isLoading: isSubmittingDelete, mutate: submitDelete } = useMutation({
    mutationFn: () =>
      performanceService.adminRemoveReviewTemplate(activeDeletId),
    onSuccess: (data) => {
      setOpenDeleteConfirmation(false)
      invalidateQueries()
      data.message && toast.success(data.message)
    },
    onError: (error) => {
      setOpenDeleteConfirmation(false)
      onQueryError(error)
    },
  })
  //  three dots on each row

  const handleClick = (id: string) => {
    if (activeReviewId == id) {
      setActiveReviewId("")
    } else {
      navigate(`/employee/dashboard/reviews/view?id=${id}`)
    }
  }
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    // startTransition(() => {
    //   setTimeout(() => refetch(), 100)
    // })
  }

  return (
    <DashboardWrapper>
      <div className="relative">
        <div className="bg-white rounded-5px px-4 py-6 md:p-6">
          <h5 className=" text-lg text-neutral800 font-extrabold mb-4">
            Reviews
          </h5>
          <div className=" flex w-full  items-center justify-between ">
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => handleSearch(e)}
              // style={{ width: "180px" }}
              rootClassName="w-full sm:w-[250px]"
            />
          </div>

          <div className="mt-2">
            <AppTable
              columns={TableColumns}
              dataSource={TableData}
              loading={isReviewRoundsLoading}
              className="text-[#42526D] !font-avenir"
              scroll={{ x: `100%` }}
            />
          </div>
        </div>

        <NewModalWrapper
          open={openDeleteConfirmation}
          onClose={() => {
            setOpenDeleteConfirmation(false)
            setActiveDeleteId("")
          }}
        >
          <div className=" flex flex-col items-center">
            <div>
              <InformationCircleIcon
                width={70}
                height={70}
                className="text-[#FAB941]"
              />
            </div>

            <h6 className="text-[#15294B]  font-bold text-2xl my-3">
              Delete Template
            </h6>
            <p className="text-[#15294B] text-sm mb-6 text-center">
              If you delete this template,it would be removed from your list.
              Are you sure you want to continue?
            </p>
          </div>
          <div className="flex gap-4 ">
            <Button
              title="cancel"
              color="danger"
              className="w-full"
              onClick={() => {
                setOpenDeleteConfirmation(false)
                setActiveDeleteId("")
              }}
            >
              Cancel
            </Button>
            {isSubmittingDelete ? (
              <Button
                title="Next"
                color="primary"
                className="font-extrabold w-full"
              >
                <LoadingIndicatorWhite />
              </Button>
            ) : (
              <Button
                title="remove"
                color="primary"
                className="w-full"
                onClick={() => {
                  submitDelete()
                }}
              >
                Confirm
              </Button>
            )}
          </div>
        </NewModalWrapper>
      </div>
    </DashboardWrapper>
  )
}
export default ViewReviewListing
